import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

interface Props {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  totalRows: number;
  onPageChange: (page: number) => void;
}

/**
 * Compact pager for the Preview step's narrow product-list panel —
 * "{from}–{to} of {total}" plus "Page {p} of {pages}" and prev/next icon
 * buttons, no numbered page links. `FullPagination` (the app's general-
 * purpose pager) renders numbered links sized for a data table; in this
 * ~300px rail they clip past the second page, which is the exact problem
 * this replaces it for — not a data-table concern, a narrow-list one.
 */
export function DeliveryWizardPreviewPager({ page, pageSize, totalRows, onPageChange }: Props) {
  const { t } = useTranslation('common');
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const from = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalRows);

  return (
    <div
      className="flex items-center gap-2 text-sm"
      data-testid="delivery-wizard-preview-pagination"
    >
      <span
        className="text-muted-foreground shrink-0 tabular-nums"
        data-testid="delivery-wizard-preview-pagination-range"
      >
        {t('deliveryWizard.preview.pager.range', { from, to, total: totalRows })}
      </span>
      <span
        className="text-muted-foreground ml-auto shrink-0 tabular-nums"
        data-testid="delivery-wizard-preview-pagination-page"
      >
        {t('deliveryWizard.preview.pager.page', { page, pages: totalPages })}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0 cursor-pointer disabled:cursor-not-allowed"
        aria-label={t('deliveryWizard.preview.pager.previous')}
        data-testid="delivery-wizard-preview-pagination-previous-button"
        disabled={page <= 1}
        onClick={() => {
          onPageChange(page - 1);
        }}
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0 cursor-pointer disabled:cursor-not-allowed"
        aria-label={t('deliveryWizard.preview.pager.next')}
        data-testid="delivery-wizard-preview-pagination-next-button"
        disabled={page >= totalPages}
        onClick={() => {
          onPageChange(page + 1);
        }}
      >
        <ChevronRight className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
